import { useState, useEffect, useRef, ReactNode, ChangeEvent } from 'react'
import { Divider } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { find, isString } from 'lodash-es'

import ScrollDetector from '@/components/ScrollDetector'
import { useMultiSelect } from '@/hooks'
import { Popup, FlexRow, Button, Loader, Icon, Text } from '@/ui'
import {
  FilterSearch,
  TriggerWrapper,
  DropdownIcon,
  Content,
  Options,
  OptionsDivider,
  Actions,
  Tag,
  Placeholder,
} from './Styles'
import Option from './Option'

interface FilterDropdownProps {
  options: Item[]
  //Couldn't find any places where initial is actually used
  initial?: string[]
  initialValue?: string[]
  value?: string[]
  onBlur?: (value: string[], excluded: string[]) => void
  onChange: (values: string[]) => void
  showTags?: boolean
  placeholder?: string
  initialExcluded?: string[]
  allowExclude?: boolean
  fluid?: boolean
  margin?: string
  sensitive?: boolean
  onSensitiveToggle?: any
  reachedBottom?: any
  loading?: boolean
  disabled?: boolean
  required?: boolean
  customTriggerText?: any
  onSearchChange?: any
  saveOnCheck?: boolean
}

interface Item {
  text: string
  value: string
  content?: ReactNode
}

const FilterDropdown = ({
  options,
  initial,
  initialValue: initialValueProp,
  // only used in conjunction with final form
  value: valueProp,
  onBlur,
  onChange,
  showTags,
  placeholder,
  initialExcluded,
  allowExclude,
  fluid,
  margin,
  sensitive,
  onSensitiveToggle,
  reachedBottom,
  loading,
  disabled,
  required,
  customTriggerText,
  onSearchChange,
  saveOnCheck,
}: FilterDropdownProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [initialOptions] = useState(options)

  const [initialValue, setInitialValue] = useState(
    initialValueProp || initial || [],
  )

  const [open, setOpen] = useState(false)

  const [visible, setVisible] = useState(false)

  const [search, setSearch] = useState('')

  const [excluded, setExcluded] = useState(initialExcluded || [])

  const [value, toggleValue1, setValue] = useMultiSelect(
    initialValue || initial || [],
  )

  const toggleValue = (newValue: string) => {
    const newValues = toggleValue1(newValue)
    if (saveOnCheck) {
      onChange(newValues)
    }
  }

  /* eslint-disable */
  useEffect(() => {
    if (open && !showTags) {
      setValue(initialValue)
    }
  }, [open])

  useEffect(() => {
    if (initialValueProp) {
      setInitialValue(initialValueProp)
      setValue(value)
    }
  }, [initialValueProp])

  // Employee/user fields are saved as comma separated values in the db
  useEffect(() => {
    if (valueProp) {
      setInitialValue(isString(valueProp) ? valueProp.split(',') : valueProp)
    }
  }, [valueProp])

  // Required by Field/Query.js to rerun the field query
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(null, { searchQuery: search })
    }
  }, [search])

  const selectAll = () => {
    const newValue =
      options.length === value.length ? [] : options.map(({ value }) => value)

    setValue(newValue)
    if (saveOnCheck) {
      onChange(newValue)
    }
  }

  const close = () => {
    if (showTags) {
      onBlur && onBlur(value, excluded)
      setOpen(false)
      return
    }
    setOpen(false)
    setValue(initialValue)
  }

  const submit = () => {
    setOpen(false)
    onChange(value)
  }

  const firstValue =
    initialValue?.length === 1
      ? find(options, { value: initialValue?.[0] })
      : null

  const multipleSelectedTriggerText = customTriggerText
    ? customTriggerText(initialValue?.length)
    : t(`{{number}} selected`, { number: initialValue?.length })

  const triggerText = firstValue
    ? firstValue.content ?? firstValue.text
    : multipleSelectedTriggerText

  const fallbackText = (
    <Text.Span
      light
      style={{
        color: 'rgba(191,191,191,.87)',
      }}>
      {placeholder ?? t('None selected')}
    </Text.Span>
  )

  const selected = value?.filter(v => {
    const { text } = find(options, { value: v }) ?? {}
    return text?.toLowerCase().includes(search.toLowerCase())
  })
  const unselected = options
    .filter(o => !value.includes(o.value))
    .filter(o => {
      const { text } = find(options, { value: o.value }) ?? {}
      return text?.toLowerCase().includes(search.toLowerCase())
    })

  const Trigger = () => {
    if (showTags) {
      const selectedItems = initialOptions.filter(option =>
        value.includes(option.value),
      )

      return (
        <TriggerWrapper
          ref={inputRef}
          disabled={disabled}
          visible={!sensitive || visible}
          margin={margin}
          showTags
          fullWidth>
          {selectedItems.length === 0 && (
            <Placeholder>{placeholder}</Placeholder>
          )}
          {selectedItems.map(({ content, text, value }: Item) => (
            <Tag excluded={excluded.includes(value)}>
              {content ?? text}
              <svg
                onClick={e => {
                  e.stopPropagation()
                  toggleValue(value)
                  const updatedValue = initial
                    ? initial.filter(item => item !== value)
                    : []
                  const updatedExcluded = excluded.filter(
                    excluded => value !== excluded,
                  )
                  setExcluded(updatedExcluded)
                  onBlur && onBlur(updatedValue, updatedExcluded)
                }}
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.27378 0.454075C7.55125 0.731544 7.55125 1.18144 7.27378 1.45891L5.09663 3.63606L7.27378 5.8132C7.55125 6.09067 7.55125 6.54057 7.27378 6.81804C6.99631 7.0955 6.54641 7.09551 6.26894 6.81804L4.0918 4.64089L1.91465 6.81804C1.63718 7.09551 1.18729 7.0955 0.909816 6.81804C0.632348 6.54057 0.632347 6.09067 0.909817 5.8132L3.08696 3.63606L0.909816 1.45891C0.632341 1.18144 0.632348 0.731544 0.909816 0.454075C1.18728 0.176607 1.63718 0.1766 1.91465 0.454075L4.0918 2.63122L6.26894 0.454075C6.54642 0.1766 6.99631 0.176607 7.27378 0.454075Z"
                  fill={excluded.includes(value) ? '#fff' : '#012D3E'}
                />
              </svg>
            </Tag>
          ))}
        </TriggerWrapper>
      )
    }

    return (
      <TriggerWrapper
        disabled={disabled}
        visible={!sensitive || visible}
        title={triggerText}
        fullWidth={fluid}>
        {!(sensitive && !visible) ? (
          <div>{initialValue?.length > 0 ? triggerText : fallbackText}</div>
        ) : (
          <div>***********</div>
        )}

        {!disabled && <DropdownIcon />}
      </TriggerWrapper>
    )
  }
  const content = (
    <Content
      onMouseDown={e => e.stopPropagation()}
      width={inputRef?.current?.clientWidth}>
      {!disabled && (
        <FilterSearch
          placeholder={t('Search')}
          value={search}
          onSearchChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
        />
      )}

      <Options className="filter-dropdown-options">
        {selected?.map?.(s => {
          const { content, text, value } = find(options, { value: s }) ?? {}
          return (
            <Option
              checked
              disabled={disabled}
              showTags={allowExclude && showTags}
              excluded={excluded}
              key={value}
              id={value}
              label={content ?? text}
              setExcluded={setExcluded}
              onChange={toggleValue}
            />
          )
        })}
        {!disabled && (
          <>
            {!!selected?.length && <OptionsDivider />}

            {unselected.map(({ content, text, value }) => (
              <Option
                key={value}
                id={value}
                label={content ?? text}
                showTags={showTags}
                onChange={toggleValue}
              />
            ))}

            {loading && (
              <Loader
                inline="centered"
                size="small"
                color="gray"
                style={{
                  display: 'flex',
                  margin: 'auto',
                  marginTop: '8px',
                }}
              />
            )}
          </>
        )}
      </Options>

      <ScrollDetector
        loading={loading}
        selector=".filter-dropdown-options"
        onBottom={reachedBottom}
      />

      {!disabled && (
        <>
          {!!unselected?.length && <Divider fitted />}
          {!showTags && (
            <Actions>
              <FlexRow.Section justifyContent="flex-start">
                <Option
                  label={t('Select all')}
                  checked={options.length === value.length}
                  onChange={selectAll}
                />
              </FlexRow.Section>

              <FlexRow.Section justifyContent="flex-end" gap="0.5em">
                <Button
                  variant="transparent"
                  content={t('Clear')}
                  onClick={() => {
                    setValue([])
                    if (saveOnCheck) {
                      onChange([])
                    }
                  }}
                />

                {!saveOnCheck && (
                  <Button
                    disabled={required && !value?.length}
                    content={t('Save')}
                    onClick={submit}
                  />
                )}
              </FlexRow.Section>
            </Actions>
          )}
        </>
      )}
    </Content>
  )

  const applyBorderRadius = {
    name: 'applyBorderRadius',
    enabled: true,
    phase: 'write',
    requires: ['applyStyles'],
    fn({ state }: any) {
      if (showTags) {
        const topPositioned = state.placement === 'top'

        const container = state.elements.popper.children[0]

        container.style.borderRadius = topPositioned
          ? '3px 3px 0 0'
          : ' 0 0 3px 3px'
        container.style.borderBottom = 0
        if (inputRef.current) {
          inputRef.current.style.borderRadius = topPositioned
            ? '0 0 3px 3px'
            : '3px 3px 0 0'
        }
      }
    },
  }

  return (
    <Popup
      on="click"
      disabled={(sensitive && !visible) || (disabled && !initialValue?.length)}
      position={showTags ? 'bottom center' : 'bottom right'}
      basic={showTags}
      offset={showTags ? [0, -11] : undefined}
      open={open}
      popperModifiers={showTags ? [applyBorderRadius] : undefined}
      style={{
        padding: 0,
      }}
      onOpen={() => setOpen(true)}
      onClose={e => {
        e.stopPropagation()
        close()
      }}
      trigger={
        <div style={{ position: 'relative' }}>
          <Trigger />

          {sensitive && (
            <Icon
              button
              size="big"
              name={visible ? 'icon-sensitive-off' : 'icon-sensitive-on'}
              onClick={e => {
                e?.stopPropagation()
                onSensitiveToggle?.(e, !visible)
                setVisible(!visible)
              }}
              style={{
                position: 'absolute',
                right: disabled ? '8px' : '24px',
                top: '8px',
              }}
            />
          )}
        </div>
      }
      content={content}
    />
  )
}

export default FilterDropdown
